import { formatApplicantIdSegment } from './applicantIdFormat'
import type { CoResidentApplication, ResidentApplicationData } from '../types'

function normalizeId(value: unknown): string {
  if (value === null || value === undefined) return ''
  const text = String(value).trim()
  if (!text) return ''
  if (/^\d+$/.test(text)) return String(Number(text))
  return text
}

function toBooleanFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true'
}

/** Candidate map keys for Angular `co_resident_application[applicantId]` lookups. */
export function applicantIdLookupKeys(...ids: Array<string | number | null | undefined>): string[] {
  const keys = new Set<string>()
  for (const id of ids) {
    if (id === null || id === undefined) continue
    const raw = String(id).trim()
    if (!raw) continue
    keys.add(raw)
    if (/^\d+$/.test(raw)) {
      keys.add(String(Number(raw)))
      keys.add(formatApplicantIdSegment(raw))
    }
  }
  return [...keys]
}

/**
 * Angular `loadApplicationDetail`:
 * `applicationData = applicationAllData.co_resident_application[applicantId]`
 *
 * Map keys / `applicant_id` / route ids often differ (leading zeros, number vs string),
 * so resolve with keyed access first, then field match, then primary / first.
 */
export function resolveActiveCoResident(
  application: ResidentApplicationData | null | undefined,
  routeApplicantId?: string | number | null,
  routeUserId?: string | number | null,
): CoResidentApplication | undefined {
  if (!application) return undefined
  const coMap = (application.co_resident_application || {}) as Record<string, CoResidentApplication>
  const coResidents = Object.values(coMap)
  if (coResidents.length === 0) return undefined

  for (const key of applicantIdLookupKeys(
    routeApplicantId,
    application.applicant_id,
    application.resident_id,
    application.default_id,
  )) {
    const hit = coMap[key]
    if (hit) return hit
  }

  const targetApplicantId = normalizeId(
    routeApplicantId ?? application.applicant_id ?? application.resident_id ?? application.default_id,
  )
  const targetUserId = normalizeId(routeUserId)

  return (
    coResidents.find((co) => normalizeId(co.applicant_id) === targetApplicantId) ||
    (targetUserId
      ? coResidents.find((co) => normalizeId(co.fk_user_id) === targetUserId)
      : undefined) ||
    coResidents.find((co) => toBooleanFlag(co.is_primary)) ||
    coResidents[0]
  )
}

/**
 * Lift co-resident `progress_status` onto the root application object so Download
 * Access Confirmation can evaluate Angular's CERTIFICATE_SENT / Access Sent gate.
 */
export function liftCoResidentProgressStatus(
  application: ResidentApplicationData,
  routeApplicantId?: string | number | null,
  routeUserId?: string | number | null,
): ResidentApplicationData {
  const active = resolveActiveCoResident(application, routeApplicantId, routeUserId)
  const coProgress = (active as { progress_status?: unknown } | undefined)?.progress_status
  const rootProgress = application.progress_status

  const progress_status =
    (typeof coProgress === 'string' || typeof coProgress === 'number'
      ? String(coProgress)
      : undefined) ??
    (typeof rootProgress === 'string' || typeof rootProgress === 'number'
      ? String(rootProgress)
      : undefined)

  return {
    ...application,
    applicant_id:
      application.applicant_id ??
      active?.applicant_id ??
      application.resident_id ??
      application.default_id ??
      routeApplicantId ??
      undefined,
    ...(progress_status !== undefined ? { progress_status } : {}),
  }
}
